import { ShareAltOutlined, UserOutlined } from "@ant-design/icons";
import { ComponentProps, useMemo, useState } from "react";
import { SnippetV1State } from "pages/api/deep-links/types";
import { VzblIcon } from "src/components/VzblIcon";
import { wordify } from "src/hooks/useCommonPrefixes";
import { useSnippets } from "src/hooks/useSnippets";
import { createWordCloud } from "src/hooks/useWordCloud";
import { SearchState } from "src/search/types";
import { ClauseMenu } from "./ClauseMenu";

export function SnippetMenu(props: {
  snippets: ReturnType<typeof useSnippets>["list"];
  onChange(params?: SearchState): void;
}) {
  const { items, setFilter, prefixes } = useSnippetMenu(props);

  return (
    <ClauseMenu
      items={items}
      filterable
      onFilterChange={setFilter}
      filterSuggestions={prefixes.length > 0 ? [{ items: prefixes }] : []}
    />
  );
}

export function useSnippetMenu(props: {
  snippets: ReturnType<typeof useSnippets>["list"];
  onChange(params?: SearchState): void;
}) {
  const [filter, setFilter] = useState("");
  const items: (ComponentProps<typeof ClauseMenu>["items"][number] & {
    text: string;
  })[] = props.snippets
    .sort((a, b) =>
      `${a.group} ${a.name}`
        .toLowerCase()
        .localeCompare(`${b.group} ${b.name}`.toLowerCase())
    )
    .filter((snippet) => {
      if (!filter) return true;
      const terms = filter.trim().toLocaleLowerCase().split(" ");
      const matchable = `${snippet.group || ""} ${snippet.name} ${
        snippet.description || ""
      }`.toLowerCase();
      return terms.every((term) => matchable.includes(term));
    })
    .map((snippet) => ({
      key: snippet.id,
      text: `${snippet.group || ""} ${snippet.name} ${
        snippet.description || ""
      }`
        .toLowerCase()
        .trim(),
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div>
            {snippet.userId ? (
              <UserOutlined />
            ) : snippet.orgId ? (
              <ShareAltOutlined />
            ) : (
              <VzblIcon size={12} />
            )}
          </div>
          <span style={{ opacity: 0.5 }}>{snippet.group}</span> / {snippet.name}
        </div>
      ),
      isOption: true,
      onSelect() {
        props.onChange((snippet.json as SnippetV1State).params);
      },
    }));
  const prefixes = useMemo(() => {
    const dict: string[] = [];
    for (const words of wordify(items.map(({ text }) => text))) {
      dict.push(...words);
    }
    return createWordCloud(dict, 9);
  }, [items]);
  return { items, setFilter, prefixes };
}
